import { useEffect, useState } from "react";
import {
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Divider,
    Chip,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import axios from "axios";
import Swal from "sweetalert2";

const statusOptions = [
    { id: 0, name: "Enabled" },
    { id: 1, name: "Disabled" },
];

export default function EditCountrySyncSettingForm({ open, handleClose, rowData, fetchData }) {
    const [editData, setEditData] = useState({});

    const [errorMessage, setErrorMessage] = useState("");
    const [showHmacSecret, setShowHmacSecret] = useState(false);

    useEffect(() => {
        if (rowData) {
            setEditData(rowData);
        }
        if (open) {
            setErrorMessage("");
            setShowHmacSecret(false);
        }
    }, [rowData, open]);

    if (!rowData) return null;

    const handleEditCountrySyncSetting = async () => {
        try {
            const {
                country_id,
                laravel_base_url,
                laravel_api_key,
                laravel_hmac_secret,
                contract_docno_prefix,
                contract_group_name,
                enable_job4_touchpoint_reminder,
                enable_job5_million_product_sync,
                enable_job6_invoice_contract_sync,
                enable_job7_contract_expiry_reminder,
                enable_job8_customer_sync,
                status,
            } = editData;

            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/country-sync-settings/${rowData.country_sync_setting_id}`, {
                country_id: country_id ?? rowData.country_id,
                laravel_base_url: laravel_base_url || null,
                laravel_api_key: laravel_api_key || null,
                laravel_hmac_secret: laravel_hmac_secret || null,
                contract_docno_prefix: contract_docno_prefix || null,
                contract_group_name: contract_group_name || null,
                enable_job4_touchpoint_reminder: Number(Boolean(enable_job4_touchpoint_reminder)),
                enable_job5_million_product_sync: Number(Boolean(enable_job5_million_product_sync)),
                enable_job6_invoice_contract_sync: Number(Boolean(enable_job6_invoice_contract_sync)),
                enable_job7_contract_expiry_reminder: Number(Boolean(enable_job7_contract_expiry_reminder)),
                enable_job8_customer_sync: Number(Boolean(enable_job8_customer_sync)),
                status,
            });

            if (response.status === 200) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Country sync setting updated successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while updating the country sync setting. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Edit Country Sync Setting</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Country Details" />
                </Divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="country_name"
                            label="Country"
                            type="text"
                            fullWidth
                            disabled
                            value={rowData.country_name || ""}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            margin="dense"
                            autoComplete="off"
                            id="status"
                            select
                            label="Status"
                            type="number"
                            value={editData.status ?? rowData.status}
                            fullWidth
                            required
                            onChange={(e) => {
                                setEditData({ ...editData, status: Number(e.target.value) });
                            }}
                        >
                            {statusOptions.map((statusItem) => (
                                <MenuItem key={statusItem.id} value={statusItem.id}>
                                    {statusItem.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>

                <Divider style={{ paddingTop: "8px", paddingBottom: "4px" }}>
                    <Chip label="Integration Details" />
                </Divider>
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="laravel_base_url"
                    label="Laravel Base URL"
                    type="text"
                    fullWidth
                    value={editData.laravel_base_url ?? ""}
                    onChange={(e) => {
                        setEditData({ ...editData, laravel_base_url: e.target.value });
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="laravel_api_key"
                    label="Laravel API Key"
                    type="text"
                    fullWidth
                    value={editData.laravel_api_key ?? ""}
                    onChange={(e) => {
                        setEditData({ ...editData, laravel_api_key: e.target.value });
                    }}
                />
                <TextField
                    autoComplete="off"
                    margin="dense"
                    id="laravel_hmac_secret"
                    label="Laravel HMAC Secret"
                    type={showHmacSecret ? "text" : "password"}
                    fullWidth
                    value={editData.laravel_hmac_secret ?? ""}
                    onChange={(e) => {
                        setEditData({ ...editData, laravel_hmac_secret: e.target.value });
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton edge="end" onClick={() => setShowHmacSecret((prev) => !prev)}>
                                    {showHmacSecret ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="contract_docno_prefix"
                            label="Contract Doc No Prefix"
                            type="text"
                            fullWidth
                            value={editData.contract_docno_prefix ?? ""}
                            onChange={(e) => {
                                setEditData({ ...editData, contract_docno_prefix: e.target.value ? e.target.value.toUpperCase() : "" });
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="contract_group_name"
                            label="Contract Group Name"
                            type="text"
                            fullWidth
                            value={editData.contract_group_name ?? ""}
                            onChange={(e) => {
                                setEditData({ ...editData, contract_group_name: e.target.value });
                            }}
                        />
                    </Grid>
                </Grid>

                <Divider style={{ paddingTop: "8px", paddingBottom: "4px" }}>
                    <Chip label="Job Toggle" />
                </Divider>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(editData.enable_job4_touchpoint_reminder)}
                                    onChange={(e) => {
                                        setEditData({ ...editData, enable_job4_touchpoint_reminder: Number(e.target.checked) });
                                    }}
                                />
                            }
                            label="Enable Job 4: Touchpoint Reminder"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(editData.enable_job5_million_product_sync)}
                                    onChange={(e) => {
                                        setEditData({ ...editData, enable_job5_million_product_sync: Number(e.target.checked) });
                                    }}
                                />
                            }
                            label="Enable Job 5: Million Product Sync"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(editData.enable_job6_invoice_contract_sync)}
                                    onChange={(e) => {
                                        setEditData({ ...editData, enable_job6_invoice_contract_sync: Number(e.target.checked) });
                                    }}
                                />
                            }
                            label="Enable Job 6: Invoice Contract Sync"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(editData.enable_job7_contract_expiry_reminder)}
                                    onChange={(e) => {
                                        setEditData({ ...editData, enable_job7_contract_expiry_reminder: Number(e.target.checked) });
                                    }}
                                />
                            }
                            label="Enable Job 7: Contract Expiry Reminder"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(editData.enable_job8_customer_sync)}
                                    onChange={(e) => {
                                        setEditData({ ...editData, enable_job8_customer_sync: Number(e.target.checked) });
                                    }}
                                />
                            }
                            label="Enable Job 8: Customer Sync"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleEditCountrySyncSetting} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Update
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}